import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  HardHat, ArrowLeft, Send, MessageSquare, User, Mail,
  Phone, CheckCircle, AlertCircle, Briefcase
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPARTMENTS = [
  { value: 'general', label_en: 'General Inquiry', label_es: 'Consulta General' },
  { value: 'client_support', label_en: 'Client Support', label_es: 'Soporte al Cliente' },
  { value: 'contractor_support', label_en: 'Contractor Support', label_es: 'Soporte a Contratistas' },
  { value: 'billing', label_en: 'Billing & Payments', label_es: 'Facturación y Pagos' },
  { value: 'verification', label_en: 'License / Insurance Verification', label_es: 'Verificación de Licencia / Seguro' },
  { value: 'dispute', label_en: 'Dispute or Complaint', label_es: 'Disputa o Queja' },
  { value: 'partnerships', label_en: 'Partnerships & Press', label_es: 'Alianzas y Prensa' },
  { value: 'legal', label_en: 'Legal / Compliance', label_es: 'Legal / Cumplimiento' },
  { value: 'technical', label_en: 'Technical / Bug Report', label_es: 'Técnico / Reporte de Error' },
];

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    department: 'general',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState(null); // 'sending' | 'success' | 'error'
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', department: 'general', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  const labels = {
    en: {
      title: 'Contact Us',
      subtitle: "We're here to help. Tell us a bit about what you need and we'll route it to the right team.",
      name: 'Full Name',
      email: 'Email',
      phone: 'Phone (optional)',
      department: 'Department',
      subject: 'Subject',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      successTitle: 'Message received',
      successBody: "Thanks! Our team will reply within 1 business day. Check your inbox for a confirmation.",
      errorTitle: 'Could not send',
      required: 'Required',
      leftTitle: 'Get the right help, fast',
      leftSub: 'Every message is routed to the team that can actually solve it — no tickets bouncing between departments.',
      points: [
        { icon: MessageSquare, title: 'Fast response', body: 'Typical reply time under 4 business hours.' },
        { icon: CheckCircle, title: 'Clear routing', body: 'Pick a department — we skip the runaround.' },
        { icon: Briefcase, title: 'For pros & clients', body: 'Same form, dedicated support tracks.' },
      ],
    },
    es: {
      title: 'Contáctanos',
      subtitle: 'Estamos aquí para ayudar. Cuéntanos qué necesitas y lo enviaremos al equipo correcto.',
      name: 'Nombre Completo',
      email: 'Correo Electrónico',
      phone: 'Teléfono (opcional)',
      department: 'Departamento',
      subject: 'Asunto',
      message: 'Mensaje',
      send: 'Enviar Mensaje',
      sending: 'Enviando...',
      successTitle: 'Mensaje recibido',
      successBody: '¡Gracias! Nuestro equipo responderá en 1 día hábil. Revisa tu bandeja para confirmación.',
      errorTitle: 'No se pudo enviar',
      required: 'Requerido',
      leftTitle: 'La ayuda correcta, rápido',
      leftSub: 'Cada mensaje se enruta al equipo que realmente puede resolverlo — sin tickets rebotando entre departamentos.',
      points: [
        { icon: MessageSquare, title: 'Respuesta rápida', body: 'Tiempo típico de respuesta: menos de 4 horas hábiles.' },
        { icon: CheckCircle, title: 'Enrutamiento claro', body: 'Elige un departamento — sin vueltas.' },
        { icon: Briefcase, title: 'Pros y clientes', body: 'Mismo formulario, caminos de soporte dedicados.' },
      ],
    },
  }[lang];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Contact Us — ContractorsLab Support"
        description="Contact the ContractorsLab team. Get in touch with Client Support, Contractor Support, Billing, Verification, Legal, or any other department."
        keywords="ContractorsLab contact, contractor marketplace support, customer service"
        path="/contact"
      />
      {/* Nav */}
      <nav className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="contact-home-link">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <Link to="/" className="text-slate-300 hover:text-white font-medium text-sm flex items-center gap-1" data-testid="contact-back-link">
                <ArrowLeft className="w-4 h-4" />
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-b-4 border-orange-600">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span className="text-orange-400 font-bold uppercase tracking-wider text-sm">Contact</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[0.95]" data-testid="contact-title">
            {labels.title}
          </h1>
          <p className="text-slate-300 text-base sm:text-lg mt-4 max-w-2xl">
            {labels.subtitle}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Left info column */}
          <aside className="lg:col-span-2 space-y-6">
            {/* Phone callout */}
            <a
              href="tel:+18779368897"
              className="block bg-slate-900 text-white rounded-sm p-6 group hover:bg-slate-800 transition-colors"
              data-testid="contact-phone-callout"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-600 rounded-sm flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-bold text-orange-500 mb-0.5">
                    {lang === 'es' ? 'Llámanos' : 'Call us'}
                  </p>
                  <p className="text-2xl font-black tracking-tight">(877) 936-8897</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {lang === 'es'
                      ? 'Lun–Vie, 8am–6pm hora del Este'
                      : 'Mon–Fri, 8am–6pm Eastern'}
                  </p>
                </div>
              </div>
            </a>

            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-2xl font-black text-slate-900 mb-2 tracking-tight">
                {labels.leftTitle}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">{labels.leftSub}</p>
              <div className="space-y-5">
                {labels.points.map((pt, i) => {
                  const Icon = pt.icon;
                  return (
                    <div key={i} className="flex gap-3">
                      <div className="w-10 h-10 bg-orange-50 rounded-sm flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm mb-0.5">{pt.title}</h4>
                        <p className="text-slate-600 text-sm">{pt.body}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Right form column */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-slate-200 rounded-sm p-6 md:p-8">
              {status === 'success' ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-5 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-2">{labels.successTitle}</h3>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">{labels.successBody}</p>
                  <button
                    onClick={() => setStatus(null)}
                    className="bg-slate-900 hover:bg-orange-600 text-white font-bold uppercase text-sm tracking-wide px-6 py-3 rounded-sm transition-colors"
                    data-testid="contact-send-another"
                  >
                    {lang === 'es' ? 'Enviar otro mensaje' : 'Send another message'}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                        <User className="w-3.5 h-3.5 inline mr-1" />
                        {labels.name}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                        <Mail className="w-3.5 h-3.5 inline mr-1" />
                        {labels.email}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                        <Phone className="w-3.5 h-3.5 inline mr-1" />
                        {labels.phone}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                        <Briefcase className="w-3.5 h-3.5 inline mr-1" />
                        {labels.department}
                      </label>
                      <select
                        name="department"
                        value={form.department}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 bg-white"
                        data-testid="contact-department-select"
                      >
                        {DEPARTMENTS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d[`label_${lang}`]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                      {labels.subject}
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                      {labels.message}
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-600 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {status === 'error' && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-3 rounded-sm text-sm" data-testid="contact-error">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span><strong>{labels.errorTitle}:</strong> {error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold uppercase tracking-wide py-4 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {status === 'sending' ? labels.sending : labels.send}
                    {status !== 'sending' && <Send className="w-4 h-4" />}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
